import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Tag, Search, X } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import OptimizedImage from '@/components/OptimizedImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Comprar': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Vender': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Rentar': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Mercado Guatemala': 'bg-purple-500/20 text-purple-400 border-purple-500/30'
};

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories] = useState(['Comprar', 'Vender', 'Rentar', 'Mercado Guatemala']);

  useEffect(() => {
    fetchArticles();
  }, [selectedCategory, searchQuery]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      
      const response = await fetch(`${API_URL}/api/blog/articles?${params}`);
      const data = await response.json();
      setArticles(data.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchArticles();
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-GT', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    const wordCount = content?.replace(/<[^>]*>/g, '').split(/\s+/).length || 0;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min de lectura`;
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="blog-page">
      <SEO 
        title="Blog | Ikonic Deals - Guías y Consejos Inmobiliarios"
        description="Descubre guías, consejos y tendencias del mercado inmobiliario en Guatemala. Aprende todo sobre comprar, vender y rentar propiedades."
        keywords="blog inmobiliario guatemala, consejos comprar casa, mercado inmobiliario guatemala, guía propiedades"
        url="/blog"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/10 via-transparent to-transparent" />
        <div className="container-custom px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-1.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs uppercase tracking-widest rounded-full mb-4">
              Blog
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl text-white font-medium mb-4">
              Guías y Consejos Inmobiliarios
            </h1>
            <p className="text-[#A3A3A3] text-lg">
              Todo lo que necesitas saber sobre el mercado inmobiliario en Guatemala
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Category Filter */}
      <section className="pb-8">
        <div className="container-custom px-4 sm:px-6">
          {/* Search Box */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar artículos..."
                className="h-12 pl-12 pr-12 bg-[#121212] border-white/10 text-white placeholder:text-white/40 focus:border-[#D4AF37]"
                data-testid="blog-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </form>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              variant={selectedCategory === null ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(null)}
              className={selectedCategory === null 
                ? 'bg-[#D4AF37] text-black hover:bg-[#FFE082]' 
                : 'border-white/20 text-white hover:bg-white/10'}
              data-testid="blog-filter-all"
            >
              Todos
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat)}
                className={selectedCategory === cat 
                  ? 'bg-[#D4AF37] text-black hover:bg-[#FFE082]' 
                  : 'border-white/20 text-white hover:bg-white/10'}
                data-testid={`blog-filter-${cat.toLowerCase().replace(' ', '-')}`}
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12">
        <div className="container-custom px-4 sm:px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-[16/10] bg-[#1E1E1E] rounded-xl" />
                  <Skeleton className="h-4 w-24 bg-[#1E1E1E]" />
                  <Skeleton className="h-6 w-full bg-[#1E1E1E]" />
                  <Skeleton className="h-4 w-3/4 bg-[#1E1E1E]" />
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#A3A3A3] text-lg">No hay artículos disponibles</p>
              {selectedCategory && (
                <Button
                  variant="outline"
                  onClick={() => setSelectedCategory(null)}
                  className="mt-4 border-[#D4AF37] text-[#D4AF37]"
                >
                  Ver todos los artículos
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                  data-testid={`blog-article-${article.slug}`}
                >
                  <Link to={`/blog/${article.slug}`}>
                    {/* Cover Image */}
                    <div className="aspect-[16/10] rounded-xl overflow-hidden mb-4 bg-[#1a1a1a]">
                      {article.cover_image ? (
                        <OptimizedImage
                          src={article.cover_image}
                          alt={article.title}
                          className="w-full h-full group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 flex items-center justify-center">
                          <span className="text-[#D4AF37]/50 text-6xl">ID</span>
                        </div>
                      )}
                    </div>

                    {/* Category & Meta */}
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-2.5 py-1 text-xs rounded-full border ${CATEGORY_COLORS[article.category] || CATEGORY_COLORS['Comprar']}`}>
                        {article.category}
                      </span>
                      <span className="text-[#666] text-xs flex items-center gap-1">
                        <Clock size={12} />
                        {estimateReadTime(article.content)}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-lg text-white font-medium mb-2 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                      {article.title}
                    </h2>

                    {/* Description */}
                    <p className="text-[#A3A3A3] text-sm line-clamp-2 mb-3">
                      {article.meta_description}
                    </p>

                    {/* Date & Read More */}
                    <div className="flex items-center justify-between">
                      <span className="text-[#666] text-xs flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(article.published_at)}
                      </span>
                      <span className="text-[#D4AF37] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Leer más <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
